import { Scissors, MapPin, Phone, Clock } from 'lucide-react';

const InstagramIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const FacebookIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
  </svg>
);

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Services', href: '#services' },
  { label: 'About Us', href: '#about' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'Book Now', href: '#booking' },
];

const serviceLinks = [
  'Hair Braiding & Styling',
  'Natural Hair Treatment',
  'Hair Coloring & Highlights',
  'Relaxer & Keratin',
  'Facials & Skincare',
  'Manicure & Pedicure',
  'Massage & Spa',
  'Makeup & Bridal',
];

const Divider = () => (
  <div className="flex items-center gap-3 my-2">
    <div className="flex-1 h-px bg-white/10" />
    <span className="text-roseGold text-xs">✦</span>
    <div className="flex-1 h-px bg-white/10" />
  </div>
);

export default function Footer() {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer style={{ background: '#111010' }} className="text-white">
      {/* Top decorative rose gold bar */}
      <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-roseGold to-transparent opacity-60" />

      {/* Main footer body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">

          {/* Col 1 — Brand */}
          <div className="lg:col-span-1">
            {/* Logo */}
            <div className="flex items-center gap-2 mb-5">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-roseGold to-burgundy flex items-center justify-center shadow-md shadow-roseGold/30">
                <Scissors className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
              <span className="font-playfair font-bold text-xl text-champagne">Lamisking</span>
            </div>
            <p className="font-playfair italic text-roseGold text-sm mb-4 leading-relaxed">
              "Where Beauty Meets Luxury"
            </p>
            <p className="font-inter text-white/50 text-xs leading-relaxed mb-6 max-w-xs">
              Abuja's premier destination for hair, beauty, and spa services. Trusted by 132+ clients and counting.
            </p>

            {/* Social icons */}
            <div className="flex gap-3">
              {[
                { href: '#', icon: <InstagramIcon className="w-4 h-4" />, label: 'Instagram', color: 'hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500' },
                { href: 'https://wa.me/2349018287564', icon: <WhatsAppIcon className="w-4 h-4" />, label: 'WhatsApp', color: 'hover:bg-[#25D366]' },
                { href: '#', icon: <FacebookIcon className="w-4 h-4" />, label: 'Facebook', color: 'hover:bg-blue-600' },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className={`w-9 h-9 rounded-xl border border-white/10 flex items-center justify-center text-white/50 hover:text-white ${s.color} hover:border-transparent transition-all duration-300`}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Col 2 — Quick links */}
          <div>
            <h4 className="font-playfair font-semibold text-champagne text-base mb-5 flex items-center gap-2">
              <span className="text-roseGold text-xs">✦</span> Quick Links
            </h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <button
                    onClick={() => scrollTo(link.href)}
                    className="font-inter text-white/50 text-sm hover:text-roseGold transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <span className="w-0 group-hover:w-3 h-px bg-roseGold transition-all duration-300 overflow-hidden" />
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 — Services */}
          <div>
            <h4 className="font-playfair font-semibold text-champagne text-base mb-5 flex items-center gap-2">
              <span className="text-roseGold text-xs">✦</span> Our Services
            </h4>
            <ul className="space-y-3">
              {serviceLinks.map((s) => (
                <li key={s}>
                  <button
                    onClick={() => scrollTo('#services')}
                    className="font-inter text-white/50 text-sm hover:text-roseGold transition-colors duration-200 text-left flex items-center gap-2 group"
                  >
                    <span className="w-0 group-hover:w-3 h-px bg-roseGold transition-all duration-300 overflow-hidden" />
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4 — Contact */}
          <div>
            <h4 className="font-playfair font-semibold text-champagne text-base mb-5 flex items-center gap-2">
              <span className="text-roseGold text-xs">✦</span> Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-roseGold mt-0.5 flex-shrink-0" />
                <span className="font-inter text-white/50 text-xs leading-relaxed">
                  CVS Plaza, Adetokunbo Ademola Crescent, Wuse 2, Abuja, Nigeria
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-roseGold flex-shrink-0" />
                <a
                  href="tel:+2349018287564"
                  className="font-inter text-white/50 text-xs hover:text-roseGold transition-colors"
                >
                  +234 901 828 7564
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-roseGold mt-0.5 flex-shrink-0" />
                <div className="font-inter text-white/50 text-xs leading-relaxed">
                  <p>Mon – Sat: 8:00 AM – 8:00 PM</p>
                  <p>Sunday: 10:00 AM – 6:00 PM</p>
                </div>
              </li>
            </ul>

            {/* WhatsApp mini button */}
            <a
              href="https://wa.me/2349018287564"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#25D366]/10 border border-[#25D366]/20 text-[#25D366] font-inter font-medium text-xs hover:bg-[#25D366]/20 transition-all duration-300"
            >
              <WhatsAppIcon className="w-4 h-4" />
              Chat on WhatsApp
            </a>
          </div>
        </div>

        {/* Divider */}
        <div className="mt-14 mb-6">
          <Divider />
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-inter text-white/30 text-xs text-center sm:text-left">
            © 2025 Lamisking Hair Salon &amp; Spa. All rights reserved.
          </p>
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-roseGold animate-pulse" />
            <p className="font-inter text-white/25 text-xs">
              Wuse 2, Abuja, Nigeria
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
